import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Spin, Tag, message } from 'antd';
import {
    LeftOutlined,
    ShareAltOutlined,
    RocketOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    ProjectOutlined,
    FieldTimeOutlined,
    FolderOpenOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { fetchProjectById } from '../../ProjectDashboard/api/projectDashboardApi';
import { fetchTasks } from '../api/projectManagementApi';

import ProjectOverviewTab from './ProjectOverviewTab';
import ProjectListTab from './ProjectListTab';
import ProjectFilesTab from './ProjectFilesTab';
import ShareProjectModal from '../../ProjectDashboard/ShareProjectModal';
import KanbanBoard from '../kanban';
import SprintBoard from '../sprint';
import TimelineBoard from '../timeline';
import TimelineMetrics from '../timeline/TimelineMetrics';

export default function ProjectDetailsWorkspace({
    projectId,
    initialTab = 'overview',
    onBack,
    openTaskDrawer,
    openCreateDrawer,
    onViewInPipeline,
    employees = [],
    currentAdmin = null,
    onRegisterRefresh,
}) {
    const location = useLocation();
    const history = useHistory();

    const [activeTab, setActiveTab] = useState(() => {
        const p = new URLSearchParams(location.search);
        return p.get('tab') || initialTab || 'overview';
    });

    const [visitedTabs, setVisitedTabs] = useState(() => {
        const p = new URLSearchParams(location.search);
        const initial = p.get('tab') || initialTab || 'overview';
        return new Set([initial]);
    });

    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Load Project & Scoped Tasks
    const loadProjectData = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const [projRes, tasksRes, milestonesRes] = await Promise.all([
                fetchProjectById(projectId),
                fetchTasks({ projectId, project: projectId, limit: 'all', fields: 'full' }),
                fetchTasks({ projectId, project: projectId, itemType: 'milestone', limit: 'all' }),
            ]);

            const projectData = projRes?.data?.result || projRes?.data?.data || (projRes?.data?._id ? projRes.data : (projRes?.result || null));
            if (projectData) {
                setProject(projectData);
            }

            const rawTasks = tasksRes?.data?.result || tasksRes?.data?.data || (Array.isArray(tasksRes?.data) ? tasksRes.data : (Array.isArray(tasksRes?.result) ? tasksRes.result : []));
            const rawMilestones = milestonesRes?.data?.result || milestonesRes?.data?.data || (Array.isArray(milestonesRes?.data) ? milestonesRes.data : (Array.isArray(milestonesRes?.result) ? milestonesRes.result : []));

            // Merge deduplicated tasks and milestones
            const taskMap = new Map();
            (Array.isArray(rawTasks) ? rawTasks : []).forEach((t) => {
                if (t && t._id) taskMap.set(String(t._id), t);
            });
            (Array.isArray(rawMilestones) ? rawMilestones : []).forEach((m) => {
                if (m && m._id) {
                    taskMap.set(String(m._id), { ...m, itemType: 'milestone' });
                }
            });

            setTasks(Array.from(taskMap.values()));
        } catch (err) {
            console.error('Failed to load project details workspace data:', err);
            message.error('Unable to load project workspace.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectData();
    }, [loadProjectData]);

    // Register refresh callback so parent can trigger a reload after task/milestone creation
    useEffect(() => {
        if (typeof onRegisterRefresh === 'function') {
            onRegisterRefresh(loadProjectData);
        }
    }, [onRegisterRefresh, loadProjectData]);

    // Handle tab change & sync URL query parameters
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setVisitedTabs((prev) => new Set([...prev, newTab]));
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', newTab);
        history.replace({
            pathname: location.pathname,
            search: `?${searchParams.toString()}`,
        });
    };

    // Share link
    const handleShare = () => {
        setShareModalOpen(true);
    };

    // Open Task in Drawer (normalizes task object or string ID)
    const handleTaskClick = useCallback((taskOrId) => {
        const id = (typeof taskOrId === 'object' && taskOrId !== null)
            ? (taskOrId._id || taskOrId.id || taskOrId.taskId)
            : taskOrId;
        if (id && openTaskDrawer) {
            openTaskDrawer(id);
        }
    }, [openTaskDrawer]);

    // Create Task (pre-fills project and target milestone)
    const handleCreateTask = useCallback((targetMilestoneId = null) => {
        if (openCreateDrawer) {
            openCreateDrawer('Backlog', 'task', project, targetMilestoneId);
        }
    }, [openCreateDrawer, project]);

    // Create Milestone (pre-fills project)
    const handleCreateMilestone = useCallback(() => {
        if (openCreateDrawer) {
            openCreateDrawer('Backlog', 'milestone', project, null);
        }
    }, [openCreateDrawer, project]);

    // Calculate Timeline Metrics
    const timelineMetrics = useMemo(() => {
        const today = dayjs();
        const taskList = Array.isArray(tasks) ? tasks : [];
        const regularTasks = taskList.filter((t) => t.itemType !== 'milestone');
        return {
            total: regularTasks.length,
            active: regularTasks.filter((t) => t.status === 'In Progress').length,
            upcoming: regularTasks.filter((t) => ['Backlog', 'Draft'].includes(t.status) || (t.startDate && dayjs(t.startDate).isAfter(today))).length,
            overdue: regularTasks.filter((t) => t.dueDate && !['Completed', 'Closed', 'Close', 'Cancelled'].includes(t.status) && dayjs(t.dueDate).isBefore(today, 'day')).length,
            underReview: regularTasks.filter((t) => t.status === 'Under Review').length,
            completed: regularTasks.filter((t) => ['Completed', 'Closed', 'Close'].includes(t.status)).length,
            onHold: regularTasks.filter((t) => t.status === 'On Hold').length,
        };
    }, [tasks]);

    // ── Permission Evaluation (Matches ProjectDetailsModal) ──
    const canEditProject = useMemo(() => {
        if (!project || !currentAdmin) return false;
        const role = (currentAdmin.role || currentAdmin.adminRole || '').toLowerCase();
        
        // 1. Role-based Super Access
        if (['super_admin', 'superadmin', 'admin', 'owner'].includes(role)) return true;

        // 2. Custom Granular Permissions
        const permsObj = currentAdmin.permissions || currentAdmin.customPermissions;
        if (permsObj) {
            const modules = ['Projects', 'ProjectDashboard', 'Project', 'All'];
            const hasCustom = modules.some((mod) => {
                const perms = permsObj[mod] || permsObj[mod.toLowerCase()] || permsObj[mod.toUpperCase()] || [];
                if (Array.isArray(perms)) {
                    return (
                        perms.includes('edit') ||
                        perms.includes('*') ||
                        perms.includes('all') ||
                        perms.includes('manage') ||
                        perms.includes('full')
                    );
                }
                return false;
            });
            if (hasCustom) return true;
        }

        // 3. Project Assignment Check (Assigned PM, Owner, or Creator)
        const myId = String(currentAdmin._id || currentAdmin.id || '');
        if (!myId) return false;

        const pmId = String(typeof project.projectManager === 'object' ? (project.projectManager?._id || project.projectManager?.id) : (project.projectManager || ''));
        const ownerId = String(typeof project.projectOwner === 'object' ? (project.projectOwner?._id || project.projectOwner?.id) : (project.projectOwner || project.owner?._id || project.owner || ''));
        const creatorId = String(typeof project.createdBy === 'object' ? (project.createdBy?._id || project.createdBy?.id) : (project.createdBy || ''));

        return myId === pmId || myId === ownerId || myId === creatorId;
    }, [project, currentAdmin]);

    if (loading && !project) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[500px]">
                <Spin size="large" tip="Loading project workspace..." />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="p-8 text-center bg-white rounded-2xl border border-slate-200 mt-4">
                <h3 className="text-base font-bold text-slate-800">Project Not Found</h3>
                <p className="text-xs text-slate-500 mt-1 mb-4">The requested project could not be loaded or has been deleted.</p>
                <button
                    onClick={() => onBack ? onBack() : history.push('/project-management')}
                    className="px-4 py-2 text-xs font-bold text-white bg-[#003263] rounded-xl hover:bg-[#03488A]"
                >
                    Return to Projects
                </button>
            </div>
        );
    }

    const prjCode = project.projectCode || `PRJ-${String(project._id || '').slice(-4).toUpperCase()}`;

    return (
        <div className="space-y-4 pb-12">
            {/* ── Breadcrumb: Back to Projects ── */}
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => onBack ? onBack() : history.push('/project-management')}
                    className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-[#003263] transition-colors cursor-pointer py-1"
                >
                    <LeftOutlined className="text-[10px]" />
                    <span>Back to Projects</span>
                </button>
            </div>

            {/* ── Dedicated Project Details Header ── */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Title + Metadata */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <h1 className="text-xl sm:text-2xl font-bold font-headline text-[#003263] dark:text-[#a7c8ff] tracking-tight">
                                {project.name}
                            </h1>
                            <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                {prjCode}
                            </span>
                        </div>

                        <div className="flex items-center gap-2.5 flex-wrap text-xs text-slate-500">
                            {project.category && (
                                <Tag color="blue" className="font-bold text-[11px] m-0">
                                    {project.category}
                                </Tag>
                            )}
                            <Tag color={project.durationMode === 'Ongoing' ? 'green' : 'cyan'} className="font-bold text-[11px] m-0">
                                {project.durationMode === 'Ongoing' ? '● Ongoing' : 'Fixed Duration'}
                            </Tag>
                            <span>·</span>
                            <span>
                                Project Manager: <strong className="text-slate-700 dark:text-slate-200">{project.projectManager?.name || project.projectManager || 'Unassigned'}</strong>
                            </span>
                            <span>·</span>
                            <Tag color={project.status === 'Completed' ? 'green' : project.status === 'On Hold' ? 'orange' : 'processing'} className="font-bold text-[11px] m-0">
                                {project.status || 'In Progress'}
                            </Tag>
                        </div>
                    </div>

                    {/* Right Header Actions */}
                    <div className="flex items-center gap-2.5 shrink-0">
                        {/* View in Pipeline Button */}
                        <button
                            type="button"
                            onClick={() => {
                                if (onViewInPipeline) {
                                    onViewInPipeline(project);
                                } else {
                                    history.push('/project-management', { projectId: project._id, activeNav: 'overview' });
                                }
                            }}
                            className="px-4 py-2 text-xs font-bold text-white bg-[#003263] hover:bg-[#03488A] rounded-xl transition-all shadow-xs flex items-center gap-2 active:scale-95 cursor-pointer"
                        >
                            <RocketOutlined />
                            <span>View in Pipeline</span>
                        </button>

                        {/* Share Project Button */}
                        <button
                            type="button"
                            onClick={handleShare}
                            className="px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                        >
                            <ShareAltOutlined />
                            <span>Share Project</span>
                        </button>
                    </div>
                </div>

                {/* ── 6 Tabs Navigation Strip ── */}
                <div className="flex items-center gap-2 border-t border-slate-100 dark:border-slate-800 mt-5 pt-3 overflow-x-auto">
                    {[
                        { key: 'overview', label: 'Overview', icon: <AppstoreOutlined className="text-base" /> },
                        { key: 'list', label: 'List', icon: <UnorderedListOutlined className="text-base" /> },
                        { key: 'kanban', label: 'Kanban', icon: <ProjectOutlined className="text-base" /> },
                        { key: 'sprint', label: 'Sprint', icon: <FieldTimeOutlined className="text-base" /> },
                        { key: 'timeline', label: 'Timeline', icon: <ClockCircleOutlined className="text-base" /> },
                        { key: 'files', label: 'Files', icon: <FolderOpenOutlined className="text-base" /> },
                    ].map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => handleTabChange(tab.key)}
                                className={`px-4 py-2.5 text-sm font-bold flex items-center gap-2 border-b-2 rounded-t-xl transition-all whitespace-nowrap cursor-pointer ${
                                    isActive
                                        ? 'border-[#003263] text-[#003263] dark:text-[#a7c8ff] dark:border-[#a7c8ff] bg-blue-50/60 dark:bg-blue-950/30'
                                        : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                }`}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── TAB PANES ── */}
            <div>
                {visitedTabs.has('overview') && (
                    <div className={activeTab === 'overview' ? 'block' : 'hidden'}>
                        <ProjectOverviewTab
                            project={project}
                            tasks={Array.isArray(tasks) ? tasks : []}
                            openTaskDrawer={handleTaskClick}
                            onNavigateTab={handleTabChange}
                            employees={employees}
                            currentAdmin={currentAdmin}
                            onProjectUpdated={(updated) => {
                                setProject((prev) => ({ ...prev, ...updated }));
                            }}
                        />
                    </div>
                )}

                {visitedTabs.has('list') && (
                    <div className={activeTab === 'list' ? 'block' : 'hidden'}>
                        <ProjectListTab
                            project={project}
                            tasks={Array.isArray(tasks) ? tasks : []}
                            onTaskClick={handleTaskClick}
                            onCreateTask={handleCreateTask}
                            onAddMilestone={handleCreateMilestone}
                            onRefreshTasks={loadProjectData}
                        />
                    </div>
                )}

                {visitedTabs.has('kanban') && (
                    <div className={activeTab === 'kanban' ? 'block' : 'hidden'}>
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
                            <KanbanBoard
                                tasks={Array.isArray(tasks) ? tasks : []}
                                onTaskClick={handleTaskClick}
                                onRowClick={handleTaskClick}
                            />
                        </div>
                    </div>
                )}

                {visitedTabs.has('sprint') && (
                    <div className={activeTab === 'sprint' ? 'block' : 'hidden'}>
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
                            <SprintBoard
                                tasks={Array.isArray(tasks) ? tasks : []}
                                onTaskClick={handleTaskClick}
                            />
                        </div>
                    </div>
                )}

                {visitedTabs.has('timeline') && (
                    <div className={activeTab === 'timeline' ? 'block' : 'hidden'}>
                        <div className="space-y-4">
                            <TimelineMetrics metrics={timelineMetrics} />
                            <TimelineBoard
                                tasks={Array.isArray(tasks) ? tasks : []}
                                onTaskClick={handleTaskClick}
                                onRowClick={handleTaskClick}
                            />
                        </div>
                    </div>
                )}

                {visitedTabs.has('files') && (
                    <div className={activeTab === 'files' ? 'block' : 'hidden'}>
                        <ProjectFilesTab project={project} />
                    </div>
                )}
            </div>

            {/* ── Share Project Modal ── */}
            <ShareProjectModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                project={project}
                onProjectUpdated={(updated) => {
                    setProject((prev) => ({ ...prev, ...updated }));
                }}
            />
        </div>
    );
}
